/**
 * Viewport flags via matchMedia (2026-06 nav refresh) — replaces the
 * ~100-line UA/touch/orientation sniffing that lived in
 * SideNavbarLayout. CSS media queries express the same intent
 * declaratively and re-evaluate natively on resize/rotate:
 *
 *  - isMobileDevice:    narrow viewport, OR a coarse-pointer (touch)
 *                       device up to tablet width — same population the
 *                       old UA regex was approximating.
 *  - isMobileLandscape: coarse-pointer device rotated landscape with a
 *                       short viewport (phone-in-landscape layouts).
 */
import { useEffect, useState } from "react";

const MOBILE_QUERY =
  "(max-width: 768px), ((pointer: coarse) and (max-width: 1024px))";
const MOBILE_LANDSCAPE_QUERY =
  "(pointer: coarse) and (orientation: landscape) and (max-height: 600px)";

function subscribe(query: string, onChange: (matches: boolean) => void) {
  // jsdom in tests may not implement matchMedia fully; fail closed.
  if (typeof window === "undefined" || !window.matchMedia) {
    onChange(false);
    return () => {};
  }
  const mql = window.matchMedia(query);
  onChange(mql.matches);
  const handler = (e: MediaQueryListEvent) => onChange(e.matches);
  // Older Safari uses addListener — feature-detect both.
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }
  if (typeof (mql as any).addListener === "function") {
    (mql as any).addListener(handler);
    return () => (mql as any).removeListener(handler);
  }
  return () => {};
}

export function useViewportFlags(): {
  isMobileDevice: boolean;
  isMobileLandscape: boolean;
} {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isLandscapeShort, setIsLandscapeShort] = useState(false);

  useEffect(() => subscribe(MOBILE_QUERY, setIsMobileDevice), []);
  useEffect(() => subscribe(MOBILE_LANDSCAPE_QUERY, setIsLandscapeShort), []);

  return {
    isMobileDevice,
    isMobileLandscape: isMobileDevice && isLandscapeShort,
  };
}
